/* [ТЕСТ] Состояния «Студии» из хэша — те же флаги, что у прототипа (flags/fill в bot-studio.js).
   Нужны, чтобы один и тот же кадр («#studio.atabtwilight», «#studio.cpscenes.ktcolls») открывался
   и в эталоне, и в серверных экранах: без этого сверка кадров невозможна.
   Грамматика флагов и демо-запросы взяты у прототипа как есть, ничего не придумано.
   Разбор вызывается только в явном демо/QA-режиме; release со staging env не включает его. */
import { initialBrowse, type BrowseState } from './studio-browse-state';

export interface StudioFlagState {
  browse: BrowseState;
  /** Открыто меню ⇅ (флаги afm, asortm, cpm, ksortm, tsortm) */
  sortOpen: boolean;
  /** Номер кадра слайдера (slide2 → 1) */
  heroSlide?: number;
  /** Экран шаблона сразу, минуя лист (флаг pgo<id>; у эталона — S.view='app', S.appO=preOpts(a)) */
  appId?: number;
  /** Колонка прокручена на N px (флаг scroll<N>): от этого зависит класс sc липкой шапки */
  scroll?: number;
  /** Прокрутить к блоку главной (флаг blk<ключ>): эталон подводит [data-blk] к верху минус 12 px */
  block?: string;
}

const FORMATS = ['all', 'reels', 'photo', 'video', 'circle'];
const CASTS = ['all', 'one', 'two', 'many', 'kid', 'pet'];
const SECTIONS = ['circle', 'photo', 'video', 'voice', 'text', 'biz'];
const SORTS = ['pop', 'new', 'cheap', 'short', 'name'];

/** Демо-запрос поиска подборки: «Подборки» — «ром», состав — «танец» (kDemoQ прототипа) */
const collectionQuery = (top: string, cast: boolean) => (top === 'colls' ? 'ром' : cast ? 'танец' : '');

/** Разбор хэша `#studio.<флаг>.<флаг>` в состояние просмотра. Неизвестные флаги молча пропускаются. */
export function studioStateFromHash(hash: string): StudioFlagState | null {
  const parts = hash.replace(/^#/, '').split('.');
  if (parts.shift() !== 'studio' || !parts.length) return null;
  const flags = new Set(parts);
  const find = (re: RegExp) => parts.find(part => re.test(part));
  const state: BrowseState = { ...initialBrowse };
  let sortOpen = false;
  let heroSlide: number | undefined;
  let touched = false;
  const to = (view: BrowseState['view']) => {
    state.view = view;
    touched = true;
  };

  const slide = find(/^slide\d$/);
  if (slide) {
    heroSlide = Math.max(0, Number(slide.slice(5)) - 1);
    touched = true;
  }

  /* ── главная: поиск ── */
  const homeQuery: Record<string, string> = {
    hfind: 'кино',
    hempty: 'вертолёт',
    hswap: 'замена персонажа',
    hvoice: 'голосовое',
  };
  for (const [flag, query] of Object.entries(homeQuery))
    if (flags.has(flag)) {
      to('home');
      state.q = query;
    }

  /* ── «Все инструменты» ── */
  const tools = find(/^tools(circle|photo|video|voice|text|biz)?$/);
  if (tools) {
    to('tools');
    const section = tools.slice(5);
    state.section = SECTIONS.includes(section) ? section : 'all';
    state.sort = 'pop';
  }
  const toolQuery: Record<string, string> = { tfind: 'фото', tempty: 'вертолёт', tprompt: 'промпт' };
  for (const [flag, query] of Object.entries(toolQuery))
    if (flags.has(flag)) {
      to('tools');
      state.sort = 'pop';
      state.q = query;
    }
  const toolSort = find(/^tsort(new|cheap|name)$/);
  if (toolSort) {
    to('tools');
    state.sort = toolSort.slice(5);
    state.sortPicked = true;
  }
  if (flags.has('tsortm')) {
    to('tools');
    state.sort = state.sort === initialBrowse.sort ? 'pop' : state.sort;
    sortOpen = true;
  }
  if (flags.has('tq')) {
    to('tools');
    state.sort = 'pop';
    state.searchOpen = true;
  }

  /* ── каталог «Все шаблоны» ── */
  if (flags.has('apps')) to('catalog');
  if (flags.has('acolls')) {
    to('catalog');
    state.tab = 'colls';
  }
  const tab = find(/^atab\w+$/);
  if (tab) {
    to('catalog');
    state.tab = tab.slice(4);
  }
  const format = find(/^af(reels|photo|video|circle)$/);
  if (format) {
    to('catalog');
    state.format = format.slice(2);
  }
  const cast = find(/^acast(all|one|two|many|kid|pet)$/);
  if (cast) {
    to('catalog');
    state.cast = cast.slice(5);
  }
  const sort = find(/^asort(new|cheap|name)$/);
  if (sort) {
    to('catalog');
    state.sort = sort.slice(5);
    state.sortPicked = true;
  }
  if (flags.has('afm') || flags.has('asortm')) {
    to('catalog');
    sortOpen = true;
  }
  if (flags.has('aq')) {
    to('catalog');
    state.searchOpen = true;
  }
  if (flags.has('afind')) {
    to('catalog');
    state.q = 'ру';
  }
  if (flags.has('anone')) {
    to('catalog');
    state.q = 'вертолёт';
  }

  /* ── страница подборки (cp, cp<код>; aempty — пустая «Пасха») ── */
  let collection = '';
  if (flags.has('cp')) {
    to('collection');
    collection = 'pop';
  }
  /* Прототип отличает код подборки от служебных флагов проверкой по списку подборок:
     `x.find(v=>/^cp\w+$/.test(v)&&collBy(v.slice(2)))`. Списка подборок здесь нет (код отдаёт документ),
     поэтому отсекаем ровно те два флага, что заняты грамматикой: `cpm` (меню ⇅) и `cpf<формат>` (вкладка).
     Отсекать всё на `cpf` нельзя: так из разбора выпадают сами подборки на «ф» — fashion, films, family
     («studio.cpfashion.cpfphoto» открывался главной вместо страницы «Мода»). */
  const page = parts.find(
    part => /^cp\w+$/.test(part) && part !== 'cpm' && !new RegExp(`^cpf(${FORMATS.join('|')})$`).test(part),
  );
  if (page) {
    to('collection');
    collection = page.slice(2);
  }
  if (flags.has('aempty')) {
    to('collection');
    collection = 'easter';
    state.format = 'video';
  }
  if (state.view === 'collection') {
    const pageFormat = find(/^cpf(all|reels|photo|video|circle)$/);
    if (pageFormat) state.format = pageFormat.slice(3);
    const top = find(/^kt\w+$/);
    if (top && !CASTS.includes(top.slice(1))) state.top = top.slice(2);
    const pageCast = find(/^kc(all|one|two|many|kid|pet)$/);
    if (pageCast) state.cast = pageCast.slice(2);
    const short = find(/^k(all|one|two|many|kid|pet|reels|photo|video|circle)$/);
    if (short) {
      const value = short.slice(1);
      if (CASTS.includes(value)) state.cast = value;
      else if (FORMATS.includes(value)) state.format = value;
    }
    const pageSort = find(/^ksort(pop|new|cheap|short|name)$/);
    if (pageSort && SORTS.includes(pageSort.slice(5))) {
      state.sort = pageSort.slice(5);
      state.sortPicked = true;
    }
    if (flags.has('cpm') || flags.has('ksortm')) sortOpen = true;
    if (flags.has('kq')) state.searchOpen = true;
    if (flags.has('knone')) state.q = 'вертолёт';
    if (flags.has('kfind')) state.q = collectionQuery(state.top, true);
    /* подборка опознаётся по коду: документ подтянет сам экран */
    state.collection = { id: collection, code: collection } as unknown as BrowseState['collection'];
  }

  /* Прокрутка колонки: scroll<N> — на N px, blk<ключ> — к блоку главной (engine.ts:1652, 1697).
     Без неё липкая шапка не прилипает и остаётся без класса sc, а с ней расходится всё поддерево. */
  const scrolled = find(/^scroll\d+$/);
  const scroll = scrolled ? Number(scrolled.slice(6)) : undefined;
  if (scroll) touched = true;
  /* blk<ключ>: часть прежних ключей эталон уводит на «Все инструменты» (BLK_OLD), остальные — блок главной */
  const BLOCK_OLD: Record<string, string> = { tools: 'all', family: 'photo', g0: 'circle', g1: 'photo', g2: 'biz', g3: 'video', g4: 'video', g5: 'text' };
  const blockFlag = find(/^blk\w+$/);
  let block: string | undefined;
  if (blockFlag) {
    const key = blockFlag.slice(3);
    if (BLOCK_OLD[key]) {
      to('tools');
      state.section = BLOCK_OLD[key];
      state.q = '';
    } else {
      to('home');
      block = key;
    }
  }

  /* pgo<id> — открыть экран создания шаблона сразу (engine.ts:1694) */
  const go = find(/^pgo\d+$/);
  const appId = go ? Number(go.slice(3)) : undefined;
  if (appId) touched = true;

  /* Номер кадра кладём и в само состояние просмотра: StudioHome читает его оттуда
     (state.heroSlide), а рядом лежащее поле до экрана не доходило — «#studio.slide5»
     открывался на первом кадре вместо пятого. */
  if (heroSlide !== undefined) state.heroSlide = heroSlide;
  return touched ? { browse: state, sortOpen, heroSlide, appId, scroll, block } : null;
}
